import { BacklogAssertionError } from "../errors/BacklogAssertionError";
import type { BacklogClassName, BindSignature } from "./BacklogService";
import type {
	BacklogDependenciesType,
	Constructor,
} from "./di/BacklogDIContainer";
import { bind, resolve } from "./di/BacklogInstantiation";
import { loadMetadata } from "./metadata/loader/MetaLoader";

/**
 * join url path
 * @param pathnames url path
 * @returns path
 */
export function joinUrlPath(...pathnames: string[]): string {
	// normalize slash
	const path = pathnames
		.map((v) => v.replace(/(^\/)|(\/$)/g, ""))
		.filter((v) => 0 < v.length)
		.join("/");
	return `/${path}`;
}

export function parseHttpWWWAuthenticate(
	http_www_authenticate_header: string | null | undefined,
): {
	scheme: string;
	params: Map<string, string>;
} {
	const pattern = /^\s*([^\s+]+)(\s+.+)?$/;
	const match = (http_www_authenticate_header || "").match(pattern);

	if (!match) {
		return {
			scheme: "",
			params: new Map<string, string>(),
		};
	}

	const scheme = match[1];
	const params_string = match[2]?.trim() || "";

	const params_pattern = /([^\s+=,]+)(?:\=("[^"]*"|[^\s+=,]*))?/g;
	const params = new Map<string, string>();

	let lastIndex = -1;
	while (true) {
		const param_match = params_pattern.exec(params_string);
		if (param_match === null || lastIndex === params_pattern.lastIndex) {
			break;
		}
		lastIndex = params_pattern.lastIndex;
		params.set(param_match[1], param_match[2] ?? "");
	}

	return {
		scheme: scheme,
		params: params,
	};
}

export function parseHttpContentType(
	http_content_type_header: string | null | undefined,
): {
	mediaType: string;
	params: Map<string, string>;
} {
	const pattern = /^([^;]+)(?:;\s*(.*))?$/;
	const match = (http_content_type_header || "").match(pattern);

	if (!match) {
		return {
			mediaType: "",
			params: new Map<string, string>(),
		};
	}

	const mediaType = match[1].trim();
	const params_string = match[2] ? match[2].trim() : "";

	const params_pattern = /(\w+)=([^;]+)/g;
	const params = new Map<string, string>();

	while (true) {
		const param_match = params_pattern.exec(params_string);
		if (param_match === null) {
			break;
		}
		params.set(param_match[1].trim(), param_match[2].trim());
	}

	return {
		mediaType: mediaType,
		params: params,
	};
}

export function createURLSearchParams<T extends object>(
	condition: T,
	exclude?: string[],
): URLSearchParams {
	const param = new URLSearchParams();

	const exclude_prop = exclude ?? [];

	Object.keys(condition).forEach((key, _) => {
		if (!exclude_prop.includes(key)) {
			const value = condition[key as keyof T];
			if (Array.isArray(value)) {
				value.forEach((item, _) => param.append(`${key}[]`, item));
			} else if (value !== undefined) {
				param.append(key, String(value));
			}
		}
	});

	return param;
}

export function cloneHeader(
	headers: Headers | [string, string][] | undefined,
): Headers {
	if (!headers) {
		return new Headers();
	}
	if (headers instanceof Headers) {
		return new Headers(Array.from(headers.entries()));
	}
	return new Headers(headers);
}

export const resolveBacklogContainer: BindSignature = <T>(
	className: BacklogClassName,
	dependencies?: object,
): T => {
	return resolve(className, dependencies);
};

export function bindBacklogContainer(
	className: BacklogClassName,
	classRef: Constructor<unknown>,
	dependencies?: BacklogDependenciesType,
): void {
	bind(className, classRef, dependencies);
}

export function assert<T>(condition: T): asserts condition is NonNullable<T> {
	if (!condition) {
		throw new BacklogAssertionError();
	}
}

export function loadBacklogMetadata(): void {
	loadMetadata();
}

// export function isBrowser(){
// 	return ![typeof window, typeof document].includes('undefined');
// }

// export function mergeHeader(
// 	...headers: (Headers | [string, string][] | undefined)[]
// ): Headers {
// 	const header = new Headers();
// 	headers.forEach((h, _) => {
// 		if (!h) {
// 			return;
// 		}

// 		let list: [string, string][] = [];
// 		if (h instanceof Headers) {
// 			list = Array.from(h.entries());
// 		} else {
// 			list = h;
// 		}
// 		list.forEach((v, _) => {
// 			header.append(v[0], v[1]);
// 		});
// 	});
// 	return header;
// }
